import React, { useState } from "react";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { SvgIcon } from "../components/SvgIcon";
import { SettingsSidebar } from "../components/SettingsSidebar";
import { AppearanceSettings } from "../components/settings/AppearanceSettings";
import { SecuritySettings } from "../components/settings/SecuritySettings";
import { ProfileSettings } from "../components/settings/ProfileSettings";
import { NotificationsSettings } from "../components/settings/NotificationsSettings";
import { PaymentsSettings } from "../components/settings/PaymentsSettings";
import { DomainSettings } from "../components/settings/DomainSettings";
import { ExternalClient } from "../components/settings/ExternalClient";
import { DataExport } from "../components/settings/DataExport";
import type { DateFormat, OpenMode } from "../../config/ui";
import type { ThemeName } from "../theme";

type SettingsSection = "profile" | "security" | "notifications" | "appearance" | "payments" | "domains" | "advanced" | "api" | "external-client";

interface SettingsViewProps {
  locale: Locale;
  theme: ThemeName;
  openMode: OpenMode;
  timezone: string;
  dateFormat: DateFormat;
  dataMode: "mock" | "api";
  onSetTheme: (theme: ThemeName) => void;
  onSetOpenMode: (mode: OpenMode) => void;
  onSetTimezone: (tz: string) => void;
  onSetDateFormat: (fmt: DateFormat) => void;
  onSetDataMode: (mode: "mock" | "api") => void;
  onBack: () => void;
}

export function SettingsView(props: SettingsViewProps) {
  const [activeSection, setActiveSection] = useState<SettingsSection>("appearance");

  const sections: Array<{ id: SettingsSection; label: string; iconName: string }> = [
    { id: "profile", label: t(props.locale, "settings.nav.profile"), iconName: "person" },
    { id: "security", label: t(props.locale, "settings.nav.security"), iconName: "security" },
    { id: "notifications", label: t(props.locale, "settings.nav.notifications"), iconName: "bell" },
    { id: "appearance", label: t(props.locale, "settings.nav.appearance"), iconName: "palette" },
    { id: "payments", label: t(props.locale, "settings.nav.payments"), iconName: "credit-card" },
    { id: "domains", label: t(props.locale, "settings.nav.domains"), iconName: "globe" },
    { id: "advanced", label: t(props.locale, "settings.nav.advanced"), iconName: "advanced" },
    { id: "api", label: t(props.locale, "settings.nav.api"), iconName: "api-key" },
    { id: "external-client", label: t(props.locale, "settings.nav.externalClient"), iconName: "external-client" }
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--surface)" }}>
      {/* Left mail nav stays visible */}
      <div style={{ width: "84px", background: "var(--canvas)", borderRight: "1px solid var(--line)", padding: "14px 0", display: "flex", flexDirection: "column", gap: "8px" }}>
        <button
          onClick={props.onBack}
          style={{
            padding: "12px 6px",
            border: "none",
            background: "transparent",
            color: "var(--muted)",
            cursor: "pointer",
            fontSize: "20px",
            borderRadius: "8px",
            transition: "background-color 0.2s"
          }}
          title={t(props.locale, "settings.backToInbox")}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hover-bg)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <SvgIcon name="arrow-left" title="Back" />
        </button>
      </div>

      {/* Settings sidebar */}
      <SettingsSidebar
        sections={sections}
        activeSection={activeSection}
        onSelectSection={(section) => setActiveSection(section as SettingsSection)}
      />

      {/* Content area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "32px 40px", maxWidth: "900px" }}>
        {activeSection === "appearance" && (
          <AppearanceSettings
            locale={props.locale}
            theme={props.theme}
            openMode={props.openMode}
            timezone={props.timezone}
            dateFormat={props.dateFormat}
            dataMode={props.dataMode}
            onSetTheme={props.onSetTheme}
            onSetOpenMode={props.onSetOpenMode}
            onSetTimezone={props.onSetTimezone}
            onSetDateFormat={props.onSetDateFormat}
            onSetDataMode={props.onSetDataMode}
          />
        )}

        {activeSection === "security" && <SecuritySettings locale={props.locale} />}

        {activeSection === "profile" && <ProfileSettings locale={props.locale} />}

        {activeSection === "notifications" && <NotificationsSettings locale={props.locale} />}

        {activeSection === "payments" && <PaymentsSettings locale={props.locale} />}

        {activeSection === "domains" && <DomainSettings locale={props.locale} />}

        {activeSection === "advanced" && (
          <DataExport locale={props.locale} />
        )}

        {activeSection === "api" && (
          <div>
            <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(props.locale, "settings.api.title")}</h2>
            <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>{t(props.locale, "settings.api.comingSoon")}</p>
            <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "40px 20px", textAlign: "center", color: "var(--muted)" }}>
              {t(props.locale, "settings.api.placeholder")}
            </div>
          </div>
        )}

        {activeSection === "external-client" && (
          <ExternalClient locale={props.locale} />
        )}
      </div>
    </div>
  );
}
